// Core components
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject, OnChanges,
  OnDestroy, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { MatInput } from '@angular/material';
import {
  DateAdapter, MatDateFormats, MAT_DATE_FORMATS, MAT_DATE_LOCALE
} from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';

// Third party components
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter
} from '@angular/material-moment-adapter';
import { FieldType } from '@ngx-formly/material';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM',
  },
  display: {
    dateInput: 'DD/MM',
    monthYearLabel: 'MMM',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

/** @title Datepicker with custom calendar header */
@Component({
  selector: 'formly-day-month-picker',
  styleUrls: ['day-month-picker.scss'],
  templateUrl: 'day-month-picker.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class DayMonthPickerComponent extends FieldType implements OnInit, OnChanges, OnDestroy {
  @ViewChild(MatInput, { static: false }) formFieldControl: MatInput;
  dayMonthPickerHeader = DayMonthPickerHeaderComponent;

  constructor(
  ) {
    super();
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();

  }
  
  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit() {
    super.ngAfterViewInit();
  }
  
   /**
   * Listen for @Input changes
   *
   * @since 1.0.0
   */
   ngOnChanges(changes: SimpleChanges): void {
    // console.log("changes", changes)
  }
  
  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
  }
}

/** Custom header component for datepicker. */
@Component({
  selector: 'day-month-picker-header',
  styles: [
    `
    .day-month-picker-header {
      display: flex;
      align-items: center;
      padding: 0.5em;
    }

    .day-month-picker-header-label {
      flex: 1;
      height: 1em;
      font-weight: 500;
      text-align: center;
    }

    .day-month-picker-double-arrow .mat-icon {
      margin: -22%;
    }
  `,
  ],
  template: `
    <div class="day-month-picker-header">
       <button mat-icon-button (click)="previousClicked('month')">
        <mat-icon fontSet="fal" fontIcon="fa-long-arrow-left"></mat-icon>
      </button>
      <span class="day-month-picker-header-label">{{periodLabel}}</span>
      <button mat-icon-button (click)="nextClicked('month')">
        <mat-icon fontSet="fal" fontIcon="fa-long-arrow-right"></mat-icon>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DayMonthPickerHeaderComponent<D> implements OnDestroy {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef
  ) {
    _calendar.stateChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => cdr.markForCheck());
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get periodLabel() {
    return this._dateAdapter
      .format(
        this._calendar.activeDate,
        this._dateFormats.display.monthYearLabel
      )
      .toLocaleUpperCase();
  }

  previousClicked(mode: any) {
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(
      this._calendar.activeDate,
      -1
    );
  }

  nextClicked(mode: any) {
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(
      this._calendar.activeDate,
      1
    );
  }
}
